import React, { useEffect, useMemo } from "react";
import { useHistory } from "react-router";
import axios, { CancelTokenSource } from "axios";
import { useDispatch } from "react-redux";

import { KButton, KDialog, KIcon } from "@kyburz/kdesign-system";
import { ReportConfig, ReportConfigsColumn } from "./typings";
import {
  deleteReportConfig,
  getReportConfigs,
} from "../../apiCalls/admin/report_configs";
import { setAlert, setReportsConfigToEdit } from "../../redux/actions/actions";
import { useStyles } from "./styles";

import useSetState from "../../hooks/useTypedSetState";
import BaseLayout from "../../components/layouts/BaseLayout";
import TableDropdown from "../../components/TableDropdown";
import KInfiniteTable from "../../components/table/KInfiniteTable";

const defaultSortingRules = {
  id: "name",
  order: "asc",
};

const ReportConfigsPage = () => {
  const { CancelToken } = axios;
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();

  const [
    {
      items,
      cancelTokenSource,
      hasNextPage,
      isNextPageLoading,
      pagesLoaded,
      order,
      reload,
      loading,
      configToDelete
    },
    setState,
  ] = useSetState({
    items: [] as ReportConfig[],
    cancelTokenSource: null as CancelTokenSource | null,
    hasNextPage: true,
    isNextPageLoading: false,
    pagesLoaded: 0,
    order: defaultSortingRules,
    configToDelete: null as string | null,
    reload: false,
    loading: false,
  });

  const fetchReportConfigs = (page: number, refresh: boolean = true): void => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel("Search cancelled");
    }
    const source = CancelToken.source();
    const sourceToken = source.token;
    setState({
      isNextPageLoading: true,
      cancelTokenSource: source,
      loading: true,
    });
    const previousArray = refresh ? [] : items;
    getReportConfigs(page, sourceToken, order.id, order.order)
      .then((res) => {
        if (!res || !res?.data) return;
        const {
          data: { data },
          hasNextPage: resHasNextPage,
        } = res;
        setState({
          items: [...previousArray, ...data],
          isNextPageLoading: false,
          pagesLoaded: page,
          loading: false,
          hasNextPage: resHasNextPage,
        });
      })
      .finally(() => {
        setState({
          isNextPageLoading: false,
        });
      });
  };

  useEffect(() => fetchReportConfigs(1, true), [order]);

  useEffect(() => {
    if (reload) {
      setState({ reload: false });
      fetchReportConfigs(1, true);
    }
  }, [reload]);

  const loadNextPage = () => fetchReportConfigs(pagesLoaded + 1, false);

  const sortTable = (data) => {
    const rules = data[0];
    if (rules) {
      setState({
        order: {
          id: rules.id,
          order: rules.desc ? "desc" : "asc",
        },
      });
    }
  };

  const onModalClose = (withReload = false) =>
    setState({ configToDelete: null, reload: withReload });

  const removeConfig = (configId) => {
    deleteReportConfig(configId)
      .then((res) => {
        if (res?.data) {
          dispatch(setAlert(`Removed report config ${configId}`, "success"));
          setState({ reload: true });
        }
      })
      .catch((e) => {
        setAlert(e.message, "error");
      })
      .finally(onModalClose);
  };

    const onConfigEdit = (config: ReportConfig) => {
      dispatch(setReportsConfigToEdit(config));
      history.push(`/config/report_configs/edit/${config._id}/editor`);
    };

  const renderTableActions = (row: ReportConfig) => (
    <TableDropdown
      menuItems={[
        {
          icon: "edit",
          title: "Edit",
          action: () => onConfigEdit(row),
        },
        {
          icon: "trash-2",
          title: "Delete",
          action: () => setState({configToDelete: row._id}),
        },
      ]}
    />
  );

  const columns = useMemo(
    () => [
      {
        id: ReportConfigsColumn.ReportType,
        Header: "Report type",
        accessor: "report_type",
        defaultCanSort: true,
        disableFilters: true,
      },
      {
        id: ReportConfigsColumn.Companies,
        Header: "Companies",
        accessor: (config: ReportConfig) => config.companies.join(", "),
        defaultCanSort: true,
        disableFilters: true,
      },
      {
        id: ReportConfigsColumn.Id,
        Header: "Id",
        accessor: "_id",
        defaultCanSort: false,
        disableFilters: true,
      },
      {
        id: ReportConfigsColumn.Actions,
        Header: "",
        accessor: "",
        minWidth: 50,
        width: "30%",
        Cell: (row) => renderTableActions(row.row.original),
        disableFilters: true,
        sticky: "right",
      },
    ],
    [],
  );

    const deletePopup = (
      <KDialog
        size="xs"
        title="Delete report config"
        open={!!configToDelete}
        primaryCTAonClick={() => removeConfig(configToDelete)}
        primaryCTAColor="danger"
        primaryCTAText="Delete"
        secondaryCTAonClick={() => onModalClose(false)}
        secondaryCTAText="Cancel"
      >
        <p>Are you sure you want delete report config {configToDelete}?</p>
      </KDialog>
    );

  return (
    <BaseLayout title="Report Configs (v2)">
      <div className={classes.ctaContainer}>
        <KButton
          icon={<KIcon icon="plus" />}
          onClick={() => history.push("/config/report_configs/create/editor")}
        >
          Create Report Config
        </KButton>
      </div>

      <div className={classes.tableContainer}>
        <KInfiniteTable
          data={items}
          hasNextPage={hasNextPage}
          isNextPageLoading={isNextPageLoading}
          loadNextPage={loadNextPage}
          columns={columns}
          manualSortBy={true}
          initialSortBy={[defaultSortingRules]}
          sortTable={sortTable}
          hasStickyColumns={true}
          loading={loading}
        />
      </div>
      {deletePopup}
    </BaseLayout>
  );
};

export default ReportConfigsPage;
